// frontend/apps/demo/piwa-portfolio/src/ui/primitives/Accordion.tsx
import React, { useState, createContext, useContext, ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import clsx from 'clsx';

// Context for Accordion state management
interface AccordionContextType {
  openItems: string[];
  toggleItem: (id: string) => void;
  multipleOpen: boolean;
}

const AccordionContext = createContext<AccordionContextType | null>(null);

// Main Accordion Component
interface AccordionProps {
  children: ReactNode;
  multipleOpen?: boolean;
  defaultOpen?: string[];
  className?: string;
  colorTheme?: 'stone' | 'moss' | 'cave' | 'ice' | 'berry' | 'volcano' | 'jungle';
}

export const Accordion: React.FC<AccordionProps> = ({
  children,
  multipleOpen = false,
  defaultOpen = [],
  className,
  colorTheme = 'stone'
}) => {
  const [openItems, setOpenItems] = useState<string[]>(defaultOpen);

  const toggleItem = (id: string) => {
    if (multipleOpen) {
      setOpenItems(prev => 
        prev.includes(id) 
          ? prev.filter(item => item !== id)
          : [...prev, id]
      );
    } else {
      setOpenItems(prev => prev.includes(id) ? [] : [id]);
    }
  };

  const themeClasses = {
    stone: 'bg-stone-800 border-stone-700',
    moss: 'bg-stone-800/90 border-moss-600',
    cave: 'bg-stone-900 border-cave-600',
    ice: 'bg-stone-800/80 border-ice-light',
    berry: 'bg-stone-800/90 border-berry-600',
    volcano: 'bg-stone-900 border-volcano-600',
    jungle: 'bg-stone-800/90 border-jungle-600'
  };

  return (
    <AccordionContext.Provider value={{ openItems, toggleItem, multipleOpen }}>
      <div 
        className={clsx(
          'space-y-2 rounded-stone border-2',
          themeClasses[colorTheme],
          className
        )}
      >
        {children}
      </div>
    </AccordionContext.Provider>
  );
};

// Accordion Item Component
interface AccordionItemProps {
  id: string;
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  badge?: ReactNode;
  tooltip?: string;
  children: ReactNode;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  disabled?: boolean;
  colorTheme?: 'stone' | 'moss' | 'cave' | 'ice' | 'berry' | 'volcano' | 'jungle';
}

export const AccordionItem: React.FC<AccordionItemProps> = ({
  id,
  title,
  subtitle,
  icon,
  badge,
  tooltip,
  children,
  className,
  headerClassName,
  contentClassName,
  disabled = false,
  colorTheme = 'stone'
}) => {
  const context = useContext(AccordionContext);
  if (!context) {
    throw new Error('AccordionItem must be used within an Accordion');
  }

  const { openItems, toggleItem } = context;
  const isOpen = openItems.includes(id);

  const themeColors = {
    stone: {
      header: 'bg-stone-700 hover:bg-stone-600 text-bone-200',
      content: 'bg-stone-800/50',
      active: 'bg-stone-600 border-moss-600'
    },
    moss: {
      header: 'bg-moss-700/30 hover:bg-moss-600/40 text-moss-300',
      content: 'bg-stone-800/30',
      active: 'bg-moss-600/50 border-moss-400'
    },
    cave: {
      header: 'bg-cave-700/30 hover:bg-cave-600/40 text-cave-300',
      content: 'bg-stone-900/50',
      active: 'bg-cave-600/50 border-cave-400'
    },
    ice: {
      header: 'bg-ice-dark/30 hover:bg-ice-dark/40 text-ice-light',
      content: 'bg-stone-800/30',
      active: 'bg-ice-dark/50 border-ice-light'
    },
    berry: {
      header: 'bg-berry-700/30 hover:bg-berry-600/40 text-berry-300',
      content: 'bg-stone-800/30',
      active: 'bg-berry-600/50 border-berry-400'
    },
    volcano: {
      header: 'bg-volcano-700/30 hover:bg-volcano-600/40 text-volcano-300',
      content: 'bg-stone-900/50',
      active: 'bg-volcano-600/50 border-volcano-400'
    },
    jungle: {
      header: 'bg-jungle-700/30 hover:bg-jungle-600/40 text-jungle-300',
      content: 'bg-stone-800/30',
      active: 'bg-jungle-600/50 border-jungle-400'
    }
  };

  const colors = themeColors[colorTheme];

  return (
    <div className={clsx('border-2 border-transparent rounded-stone overflow-hidden', className)}>
      <button
        onClick={() => !disabled && toggleItem(id)}
        disabled={disabled}
        className={clsx(
          'w-full px-4 py-3 flex items-center justify-between transition-all duration-200',
          isOpen ? `${colors.active} border-2` : colors.header,
          disabled && 'opacity-50 cursor-not-allowed',
          headerClassName
        )}
      >
        <div className="flex items-center gap-3 flex-1">
          <motion.div
            animate={{ rotate: isOpen ? 90 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronRight className="w-4 h-4" />
          </motion.div>
          
          {icon && <div className="w-5 h-5">{icon}</div>}
          
          <div className="text-left">
            <div className="font-display text-sm font-bold">{title}</div>
            {subtitle && (
              <div className="text-xs opacity-70 mt-0.5">{subtitle}</div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {badge}
          {tooltip && (
            <div className="group relative">
              <Info className="w-4 h-4 text-moss-400" />
              <div className="absolute right-0 bottom-full mb-2 hidden group-hover:block z-50 w-48 p-2 bg-stone-900 border-2 border-moss-600 rounded-stone text-xs text-bone-200 shadow-lg">
                {tooltip}
              </div>
            </div>
          )}
        </div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className={clsx('overflow-hidden', colors.content)}
          >
            <div className={clsx('p-4', contentClassName)}>
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Accordion Group Component (for organizing multiple accordions)
interface AccordionGroupProps {
  children: ReactNode;
  title?: string;
  className?: string;
}

export const AccordionGroup: React.FC<AccordionGroupProps> = ({
  children,
  title,
  className
}) => {
  return (
    <div className={clsx('space-y-4', className)}>
      {title && (
        <h3 className="font-display text-lg text-moss-400 border-b-2 border-stone-700 pb-2">
          {title}
        </h3>
      )}
      {children}
    </div>
  );
};

// Accordion Section (for nested content within items)
interface AccordionSectionProps {
  title?: string;
  children: ReactNode;
  className?: string;
}

export const AccordionSection: React.FC<AccordionSectionProps> = ({
  title,
  children,
  className
}) => {
  return (
    <div className={clsx('space-y-2', className)}>
      {title && (
        <h4 className="text-xs font-bold text-moss-300 uppercase tracking-wider">
          {title}
        </h4>
      )}
      <div className="space-y-1">
        {children}
      </div>
    </div>
  );
};